import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0Data } from 'neon-auth0';
import jwt from 'jwt-decode';
import { useHistory, useLocation } from 'react-router-dom';
import ArticleList from '../../../ui/ArticleList';
import { ContainerNoScroll } from '../../../ui/ContainerNoScroll';
import {
    AVAILABLE_USER_GUIDE_SCOPES,
    USER_GUIDE_ORDER_ALL_KEY,
    USER_GUIDE_SCOPES,
} from '../../../../gql/userGuideScopes';
import { DESKS_SHORT } from '../../../../gql/desks';
import { Desk } from '../../../../shared/types';
import { getDeskLink } from '../../../../utils/link.utils';
import { ALL_USER_GUIDE_CHIPS, USER_GUIDE_BANNER_PUBLISHER } from './constants';
import { Auth0Token, UserGuideScopeData } from './types';
import {
    getSearchWithSelectedChip,
    getSelectedChipFromSearch,
    groupUserGuideScopesByName,
} from './utils/chipUtils';
import { getUserPermissions } from './utils/authUtils';
import UserGuideBanner from './components/UserGuideBanner';
import UserGuideScopeChips from './components/UserGuideScopeChips';

const UserGuidePage: React.FC = () => {
    const { accessToken } = useAuth0Data();
    const history = useHistory();
    const location = useLocation();
    const [articleRefreshVersion, setArticleRefreshVersion] = useState(0);

    const decodedToken: Auth0Token | null = accessToken
        ? jwt(accessToken)
        : null;
    const userPermissions = useMemo(
        () => getUserPermissions(decodedToken),
        [decodedToken]
    );

    const {
        data: userGuideScopeData,
        loading: userGuideScopesLoading,
        refetch: refetchUserGuideScopes,
    } = useQuery<UserGuideScopeData>(USER_GUIDE_SCOPES, {
        fetchPolicy: 'network-only',
    });
    const userGuideScopes = useMemo(
        () => userGuideScopeData?.userGuideScopes ?? [],
        [userGuideScopeData?.userGuideScopes]
    );

    const { data: desksData } = useQuery<{
        documents: Pick<Desk, '_id' | 'name'>[];
    }>(DESKS_SHORT, {
        fetchPolicy: 'cache-first',
    });

    const userGuidePublisherDeskLink = useMemo(() => {
        const matchingDesk = (desksData?.documents || []).find(
            (desk) =>
                desk.name?.trim().toLowerCase() ===
                USER_GUIDE_BANNER_PUBLISHER.toLowerCase()
        );

        return matchingDesk?._id ? getDeskLink(matchingDesk._id) : undefined;
    }, [desksData?.documents]);

    const scopeLabelByScope = useMemo(
        () =>
            userGuideScopes.reduce<Record<string, string>>((acc, item) => {
                const scope = item.scope?.trim();
                const name = item.name?.trim();

                if (scope && name) {
                    acc[scope] = name;
                }

                return acc;
            }, {}),
        [userGuideScopes]
    );

    const {
        data: availableScopeData,
        loading: availableScopesLoading,
        refetch: refetchAvailableScopes,
    } = useQuery<{ availableUserGuideScopes: string[] }>(
        AVAILABLE_USER_GUIDE_SCOPES,
        { fetchPolicy: 'network-only' }
    );

    useEffect(() => {
        const refreshUserGuides = () => {
            refetchUserGuideScopes?.();
            refetchAvailableScopes?.();
            setArticleRefreshVersion((version) => version + 1);
        };

        window.addEventListener('focus', refreshUserGuides);

        return () => window.removeEventListener('focus', refreshUserGuides);
    }, [refetchAvailableScopes, refetchUserGuideScopes]);

    const availableChips = useMemo(() => {
        const groupedScopes = groupUserGuideScopesByName(userGuideScopes);
        const availableScopeKeys = new Set(
            availableScopeData?.availableUserGuideScopes ?? []
        );

        return groupedScopes.filter((group) => {
            const userHasAccessToGroup = group.scopes.some((scope) =>
                userPermissions.includes(scope)
            );
            const groupHasVisibleGuides = group.scopes.some((scope) =>
                availableScopeKeys.has(scope)
            );

            return userHasAccessToGroup && groupHasVisibleGuides;
        });
    }, [
        availableScopeData?.availableUserGuideScopes,
        userGuideScopes,
        userPermissions,
    ]);

    const isChipLoading = userGuideScopesLoading || availableScopesLoading;
    const requestedChip = useMemo(
        () => getSelectedChipFromSearch(location.search, userGuideScopes),
        [location.search, userGuideScopes]
    );
    const selectedChip = useMemo(() => {
        if (isChipLoading) {
            return requestedChip;
        }

        const chipExists =
            requestedChip === ALL_USER_GUIDE_CHIPS ||
            availableChips.some((chip) => chip.name === requestedChip);

        return chipExists ? requestedChip : ALL_USER_GUIDE_CHIPS;
    }, [availableChips, isChipLoading, requestedChip]);

    useEffect(() => {
        if (isChipLoading) {
            return;
        }

        const nextSearch = getSearchWithSelectedChip(
            location.search,
            selectedChip
        );

        if (nextSearch !== location.search) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            });
        }
    }, [
        history,
        isChipLoading,
        location.pathname,
        location.search,
        selectedChip,
    ]);

    const selectChip = useCallback(
        (chip: string) => {
            const nextSearch = getSearchWithSelectedChip(location.search, chip);

            if (nextSearch !== location.search) {
                history.replace({
                    pathname: location.pathname,
                    search: nextSearch,
                });
            }
        },
        [history, location.pathname, location.search]
    );

    const selectedUserGuideScopes = useMemo(() => {
        if (selectedChip === ALL_USER_GUIDE_CHIPS) {
            const visibleScopes = Array.from(
                new Set(
                    availableChips.flatMap((chip) =>
                        chip.scopes.filter(Boolean)
                    )
                )
            );

            return [USER_GUIDE_ORDER_ALL_KEY, ...visibleScopes];
        }

        return availableChips.find((chip) => chip.name === selectedChip)
            ?.scopes;
    }, [availableChips, selectedChip]);

    return (
        <ContainerNoScroll>
            <Grid container spacing={3}>
                <Grid size={12}>
                    <UserGuideBanner
                        publishedByDeskLink={userGuidePublisherDeskLink}
                    />
                </Grid>
                <Grid size={12}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <UserGuideScopeChips
                            isLoading={isChipLoading}
                            selectedChip={selectedChip}
                            availableChips={availableChips}
                            onSelectChip={selectChip}
                        />
                    </Box>
                </Grid>
                <Grid size={12}>
                    <ArticleList
                        key={articleRefreshVersion}
                        source="userGuide"
                        showPagination
                        isUserGuideOnly
                        userGuideScopes={selectedUserGuideScopes}
                        showScopeChipOnCards={
                            selectedChip === ALL_USER_GUIDE_CHIPS
                        }
                        scopeLabelByScope={scopeLabelByScope}
                        displayArticleCount={false}
                        keepPreviousResultsWhileLoading
                        showInitialLoadingSkeleton
                        initialLoadingSkeletonCount={6}
                    />
                </Grid>
            </Grid>
        </ContainerNoScroll>
    );
};

export default UserGuidePage;
